/**
 * @fileoverview AdminDashboard Component
 *
 * Main dashboard for RSH Portal admin interface.
 * Displays project statistics, alerts, deadlines,
 * upload shortcuts, and quick actions.
 *
 * Layout:
 * - Banner: Full-width hero with project stats (via AdminPageBanner)
 * - Body Grid (2 columns desktop):
 *   Left: Project Alerts + Add New Records
 *   Right: Upcoming Deadlines + Upload Files
 *
 * Calendar/Tasks sidebar has been removed — the tasks panel is now
 * a global dropdown accessible from the navbar on every page,
 * with data owned by AdminPortal.
 *
 * @component
 * @version 4.0.0
 */

import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHouseChimneyUser,
  faHouseFlag,
  faBuilding,
} from "@fortawesome/free-solid-svg-icons";
import {
  FaSwimmingPool,
  FaExclamationTriangle,
  FaClock,
  FaClipboardList,
  FaFileSignature,
  FaCamera,
  FaFileAlt,
  FaFolderOpen,
  FaUserPlus,
  FaProjectDiagram,
  FaHardHat,
  FaChevronRight,
  FaSpinner,
} from "react-icons/fa";
import { formatDateET } from "../../utils/dateUtils";
import { formatProgress } from "../../utils/projectUtils";

// Data hooks
import {
  useAdminDashboardStats,
  useAdminAlerts,
  useUpcomingDeadlines,
} from "../../hooks/useAdminDashboard";
import {
  useCreateProject,
  useProjectManagers,
} from "../../hooks/useAdminProjects";
import { useAdminClients, useCreateClient } from "../../hooks/useAdminClients";
import { useCreateVendor } from "../../hooks/useAdminVendors";
import {
  useChangeOrder,
  useChangeOrderFieldOptions,
  useCreateChangeOrder,
  useUpdateChangeOrder,
} from "../../hooks/useChangeOrders";

// Shared components
import DashboardListModal from "../../components/admin/DashboardListModal";
import AdminPageBanner from "../../components/admin/AdminPageBanner";

// Modals
import ProjectModal from "../../components/admin/ProjectModal";
import ClientModal from "../../components/admin/ClientModal";
import VendorModal from "../../components/admin/VendorModal";
import ChangeOrderModal from "../../components/admin/ChangeOrderModal";
import PlansDocsUploadModal from "../../components/admin/PlansDocsUploadModal";
import ProgressPhotoUploadModal from "../../components/admin/ProgressPhotoUploadModal";
import SelectionDocsUploadModal from "../../components/admin/SelectionDocsUploadModal";

import "./AdminDashboard.css";

/* ============================================
   INLINE HELPER COMPONENTS
   (small presentational — no state)
   ============================================ */

/** AlertItem — single alert row with icon, label, and count badge */
const AlertItem = ({ icon, label, count, onClick, variant = "warning" }) => (
  <button
    className={`admin-dash-alert-item admin-dash-alert-${variant}`}
    onClick={onClick}
  >
    <span className="admin-dash-alert-icon">{icon}</span>
    <span className="admin-dash-alert-label">{label}</span>
    <span className="admin-dash-alert-count">{count}</span>
  </button>
);

/** DeadlineItem — single clickable deadline row */
const DeadlineItem = ({ id, projectName, dueDate, daysUntil, onClick }) => (
  <button
    className={`admin-dash-deadline-item ${daysUntil <= 7 ? "urgent" : ""}`}
    onClick={() => onClick && onClick(id)}
  >
    <div className="admin-dash-deadline-info">
      <span className="admin-dash-deadline-project">{projectName}</span>
      <span className="admin-dash-deadline-type">Selections Due</span>
    </div>
    <div className="admin-dash-deadline-date">
      <span className="admin-dash-deadline-days">
        {daysUntil === 0
          ? "Today"
          : daysUntil === 1
            ? "Tomorrow"
            : `${daysUntil} days`}
      </span>
      <span className="admin-dash-deadline-actual">
        {formatDateET(dueDate)}
      </span>
    </div>
  </button>
);

/** UploadCard — single upload action row */
const UploadCard = ({ icon, title, description, onClick }) => (
  <button className="admin-dash-upload-card" onClick={onClick}>
    <div className="admin-dash-upload-icon">{icon}</div>
    <div className="admin-dash-upload-content">
      <span className="admin-dash-upload-title">{title}</span>
      <span className="admin-dash-upload-desc">{description}</span>
    </div>
  </button>
);

/** QuickActionButton — single quick-add action row */
const QuickActionButton = ({ icon, label, onClick }) => (
  <button className="admin-dash-quick-action" onClick={onClick}>
    <span className="admin-dash-quick-action-icon">{icon}</span>
    <span className="admin-dash-quick-action-label">{label}</span>
  </button>
);

/** LoadingSpinner — inline loading indicator */
const LoadingSpinner = ({ message = "Loading..." }) => (
  <div className="admin-dash-loading">
    <FaSpinner className="admin-dash-spinner" />
    <span>{message}</span>
  </div>
);

/** ProjectTypeButton — clickable stat button for project types in banner */
const ProjectTypeButton = ({
  icon,
  count,
  label,
  shortLabel,
  onClick,
  className,
}) => (
  <button className={`admin-dash-project-type ${className}`} onClick={onClick}>
    {icon}
    <span className="type-count">{count}</span>
    <span className="type-label type-label-full">{label}</span>
    {shortLabel && (
      <span className="type-label type-label-short">{shortLabel}</span>
    )}
  </button>
);

/* ============================================
   HELPER FUNCTIONS
   ============================================ */

/**
 * Calculate days until a date
 * @param {string} dateStr - ISO date string
 * @returns {number} Days until the date (999 if no date)
 */
function calculateDaysUntil(dateStr) {
  if (!dateStr) return 999;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const targetDate = new Date(dateStr);
  targetDate.setHours(0, 0, 0, 0);
  return Math.ceil((targetDate - today) / (1000 * 60 * 60 * 24));
}

/* ============================================
   MAIN DASHBOARD COMPONENT
   ============================================ */

/**
 * AdminDashboard Component
 *
 * @param {Object} props
 * @param {Object} props.user - Firebase user object
 * @param {Object} props.userData - User data from Team table
 */
function AdminDashboard({ user, userData, showTestAccounts }) {
  const navigate = useNavigate();

  // ==================== STATE ====================

  const [listModal, setListModal] = useState({
    isOpen: false,
    title: "",
    items: [],
    renderItem: null,
    emptyMessage: "",
  });
  const [quickAddOpen, setQuickAddOpen] = useState({
    project: false,
    client: false,
    vendor: false,
  });
  const [uploadModalOpen, setUploadModalOpen] = useState({
    progressPhotos: false,
    selectionDocs: false,
    projectDocs: false,
  });
  const [coEditModal, setCoEditModal] = useState({
    changeOrderId: null,
    projectId: null,
    projectName: "",
  });

  // ==================== DATA HOOKS ====================

  const { data: statsData, isLoading: statsLoading } = useAdminDashboardStats({
    showTestAccounts,
  });
  const { data: alertsData, isLoading: alertsLoading } = useAdminAlerts({
    showTestAccounts,
  });
  const { data: deadlinesData, isLoading: deadlinesLoading } =
    useUpcomingDeadlines(30, { showTestAccounts });
  const { data: clientsList, isLoading: clientsLoading } = useAdminClients({
    showTestAccounts,
  });
  const { data: pmList, isLoading: pmLoading } = useProjectManagers();
  const createProjectMutation = useCreateProject();
  const createClientMutation = useCreateClient();
  const createVendorMutation = useCreateVendor();
  const { data: coData } = useChangeOrder(coEditModal.changeOrderId);
  const { data: coFieldOptions } = useChangeOrderFieldOptions();
  const createCOMutation = useCreateChangeOrder(coEditModal.projectId);
  const updateCOMutation = useUpdateChangeOrder(coEditModal.projectId);

  // ==================== DATA TRANSFORMS ====================

  const projectStats = useMemo(() => {
    if (!statsData)
      return {
        total: 0,
        byType: { custom: 0, spec: 0, commercial: 0, pool: 0 },
        projects: [],
      };
    return {
      total: statsData.totalActive || 0,
      byType: statsData.byType || {
        custom: 0,
        spec: 0,
        commercial: 0,
        pool: 0,
      },
      projects: statsData.projects || [],
    };
  }, [statsData]);

  const alerts = useMemo(() => {
    if (!alertsData)
      return {
        overdueTasks: { count: 0, items: [] },
        missingSelections: { count: 0, items: [] },
        stalledProgress: { count: 0, items: [] },
        pendingChangeOrders: { count: 0, items: [] },
      };
    return alertsData;
  }, [alertsData]);

  const deadlines = useMemo(() => {
    if (!deadlinesData) return [];
    return deadlinesData.map((d) => ({
      ...d,
      daysUntil: calculateDaysUntil(d.dueDate),
    }));
  }, [deadlinesData]);

  const clientsForDropdown = useMemo(() => {
    if (!clientsList) return [];
    return clientsList.map((c) => ({
      id: c.id,
      clientName: c.clientName || "",
      email: c.email || "",
    }));
  }, [clientsList]);

  // ==================== MODAL RENDER FUNCTIONS ====================

  /** Build a render function for alert items of a specific type */
  const getAlertRenderer = (alertType) => {
    const configs = {
      overdueTasks: {
        getTitle: (i) => i.taskName,
        getSubtitle: (i) => i.projectName || "No project",
        getDate: (i) => `Due: ${formatDateET(i.endDate)}`,
        dateClass: "overdue",
        onClick: (i) => {
          closeListModal();
          // Dispatch event for AdminPortal to handle via TaskModal
          window.dispatchEvent(
            new CustomEvent("admin-edit-task", { detail: { id: i.id } }),
          );
        },
      },
      missingSelections: {
        getTitle: (i) => i.projectName,
        getSubtitle: (i) => i.clientName,
        getBadges: (i) => [
          i.missingCount > 0
            ? { text: `${i.missingCount} needed`, tone: "alert" }
            : { text: "All selections completed", tone: "ok" },
          i.paintCount === 0
            ? { text: "Needs paint selections", tone: "alert" }
            : {
              text: `${i.paintCount} paint colors recorded`,
              tone: i.paintCount < 3 ? "warn" : "ok",
            },
        ],
        onClick: (i) => {
          closeListModal();
          navigate(`/admin/projects/${i.id}`);
        },
      },
      stalledProjects: {
        getTitle: (i) => i.projectName,
        getSubtitle: (i) => i.phase,
        getDate: (i) => `${formatProgress(i.progress)}% complete`,
        dateClass: "",
        onClick: (i) => {
          closeListModal();
          navigate(`/admin/projects/${i.id}`);
        },
      },
      pendingChangeOrders: {
        getTitle: (i) => i.projectName,
        getSubtitle: (i) => i.title || i.coNumber,
        getDate: (i) =>
          i.costImpact ? `$${i.costImpact.toLocaleString()}` : "N/A",
        dateClass: "",
        onClick: (i) => {
          closeListModal();
          setCoEditModal({
            changeOrderId: i.id,
            projectId: i.projectId || "",
            projectName: i.projectName || "",
          });
        },
      },
    };
    const config = configs[alertType];
    if (!config) return () => null;
    return (item) => (
      <button
        key={item.id}
        className="admin-dash-modal-item admin-dash-modal-item-clickable"
        onClick={() => config.onClick(item)}
      >
        <span className="modal-item-title">{config.getTitle(item)}</span>
        <span className="modal-item-subtitle">{config.getSubtitle(item)}</span>
        {config.getBadges ? (
          <span className="modal-item-badges">
            {config.getBadges(item).map((badge, idx) => (
              <span
                key={idx}
                className={`modal-item-badge badge-${badge.tone}`}
              >
                {badge.text}
              </span>
            ))}
          </span>
        ) : (
          <span className={`modal-item-date ${config.dateClass}`}>
            {config.getDate(item)}
          </span>
        )}
        <FaChevronRight className="modal-item-arrow" />
      </button>
    );
  };

  /** Render a project item in the projects-by-type modal */
  const renderProjectItem = (project) => {
    const clientDisplay = (project.type || "").toLowerCase().includes("spec")
      ? "Red Setter Homes"
      : project.clientName || "No client assigned";
    return (
      <button
        key={project.id}
        className="admin-dash-modal-item admin-dash-modal-item-clickable"
        onClick={() => {
          closeListModal();
          navigate(`/admin/projects/${project.id}`);
        }}
      >
        <span className="modal-item-title">
          {project.name || project.address}
        </span>
        <span className="modal-item-subtitle">{clientDisplay}</span>
        <span className="modal-item-date">
          {project.startDate
            ? `Started: ${formatDateET(project.startDate)}`
            : project.phase || ""}
        </span>
        <FaChevronRight className="modal-item-arrow" />
      </button>
    );
  };

  // ==================== HANDLERS ====================

  const openAlertModal = (type, title, subtitle, items, emptyMessage) => {
    setListModal({
      isOpen: true,
      title,
      subtitle,
      items,
      emptyMessage,
      renderItem: getAlertRenderer(type),
    });
  };

  const openProjectsModal = (projectType, title) => {
    const filtered = projectStats.projects.filter((p) => {
      const t = (p.type || "").toLowerCase();
      return projectType === "custom"
        ? t.includes("custom")
        : projectType === "spec"
          ? t.includes("spec")
          : projectType === "commercial"
            ? t.includes("commercial")
            : projectType === "pool"
              ? t.includes("pool")
              : true;
    });
    setListModal({
      isOpen: true,
      title,
      items: filtered,
      renderItem: renderProjectItem,
    });
  };

  const openDeadlinesModal = () => {
    setListModal({
      isOpen: true,
      title: "All Upcoming Deadlines",
      items: deadlines,
      renderItem: (d) => (
        <DeadlineItem key={d.id} {...d} onClick={handleDeadlineClick} />
      ),
      emptyMessage: "No upcoming selection deadlines",
    });
  };

  const closeListModal = () => {
    setListModal({
      isOpen: false,
      title: "",
      items: [],
      renderItem: null,
      emptyMessage: "",
    });
  };

  const handleDeadlineClick = (projectId) => {
    if (projectId) navigate(`/admin/projects/${projectId}?tab=selections`);
  };

  const openQuickAdd = (type) =>
    setQuickAddOpen((prev) => ({ ...prev, [type]: true }));
  const closeQuickAdd = (type) =>
    setQuickAddOpen((prev) => ({ ...prev, [type]: false }));

  const handleUpload = (type) => {
    const key =
      type === "progress-photos"
        ? "progressPhotos"
        : type === "selection-docs"
          ? "selectionDocs"
          : "projectDocs";
    setUploadModalOpen((prev) => ({ ...prev, [key]: true }));
  };
  const closeUploadModal = (type) =>
    setUploadModalOpen((prev) => ({ ...prev, [type]: false }));

  // ==================== RENDER ====================

  return (
    <>
      <div className="admin-dash-container">
        <div className="admin-dash-main">
          {/* Page Banner */}
          <AdminPageBanner
            title="Dashboard"
            greeting={`Welcome back, ${user?.displayName || userData?.name || "Admin"}`}
            subtitle="Here's what's happening across your projects today."
            count={projectStats.total}
            countLabel="Active Projects"
            loading={statsLoading}
          >
            {/* Project type breakdown — custom children */}
            <div className="admin-dash-projects-breakdown">
              <ProjectTypeButton
                icon={
                  <FontAwesomeIcon
                    icon={faHouseChimneyUser}
                    className="type-icon custom"
                  />
                }
                count={projectStats.byType.custom}
                label="Custom"
                shortLabel="Cust"
                className="clickable"
                onClick={() =>
                  openProjectsModal("custom", "Custom Home Projects")
                }
              />
              <ProjectTypeButton
                icon={
                  <FontAwesomeIcon
                    icon={faHouseFlag}
                    className="type-icon spec"
                  />
                }
                count={projectStats.byType.spec}
                label="Spec"
                shortLabel="Spec"
                className="clickable"
                onClick={() => openProjectsModal("spec", "Spec House Projects")}
              />
              <ProjectTypeButton
                icon={
                  <FontAwesomeIcon
                    icon={faBuilding}
                    className="type-icon commercial"
                  />
                }
                count={projectStats.byType.commercial}
                label="Commercial"
                shortLabel="Comm"
                className="clickable"
                onClick={() =>
                  openProjectsModal("commercial", "Commercial Projects")
                }
              />
              <ProjectTypeButton
                icon={<FaSwimmingPool className="type-icon pool" />}
                count={projectStats.byType.pool}
                label="Pools"
                shortLabel="Pool"
                className="clickable"
                onClick={() => openProjectsModal("pool", "Pool Projects")}
              />
            </div>
          </AdminPageBanner>

          {/* ====== BODY SECTIONS ======
              DOM order = mobile priority: alerts → deadlines → records → uploads
              Desktop CSS uses `order` to place into 2 columns:
                Left col (odd):  alerts (1), records (3)
                Right col (even): deadlines (2), uploads (4)
          */}
          <div className="admin-dash-body">
            {/* 1. Project Alerts — desktop left col, row 1 */}
            <div className="admin-dash-section admin-dash-section-alerts">
              <div className="admin-dash-section-header">
                <h2>Project Alerts</h2>
              </div>
              <div className="admin-dash-section-content">
                {alertsLoading ? (
                  <LoadingSpinner message="Checking alerts..." />
                ) : (
                  <>
                    <AlertItem
                      icon={<FaExclamationTriangle />}
                      label="Overdue Tasks"
                      count={alerts.overdueTasks?.count || 0}
                      variant={alerts.overdueTasks?.count > 0 ? "danger" : "ok"}
                      onClick={() =>
                        openAlertModal(
                          "overdueTasks",
                          "Overdue Tasks",
                          "Tasks that are past their due date",
                          alerts.overdueTasks?.items || [],
                          "No overdue tasks. Great job staying on top of things!",
                        )
                      }
                    />
                    <AlertItem
                      icon={<FaClipboardList />}
                      label="Missing Selections"
                      count={alerts.missingSelections?.count || 0}
                      variant={
                        alerts.missingSelections?.count > 0 ? "warning" : "ok"
                      }
                      onClick={() =>
                        openAlertModal(
                          "missingSelections",
                          "Missing Selections",
                          "Active Projects with upcoming or past due selection deadlines that are missing selections.",
                          alerts.missingSelections?.items || [],
                          "All clients have completed and signed off on their selections.",
                        )
                      }
                    />
                    <AlertItem
                      icon={<FaClock />}
                      label="Stalled Progress"
                      count={alerts.stalledProgress?.count || 0}
                      variant={
                        alerts.stalledProgress?.count > 0 ? "danger" : "ok"
                      }
                      onClick={() =>
                        openAlertModal(
                          "stalledProjects",
                          "Stalled Projects",
                          "Projects that have been marked as 'Delayed Construction'.",
                          alerts.stalledProgress?.items || [],
                          "No projects with 'Delayed Construction' status. Keep up the good work pushing projects forward!",
                        )
                      }
                    />
                    <AlertItem
                      icon={<FaFileSignature />}
                      label="Pending Change Orders"
                      count={alerts.pendingChangeOrders?.count || 0}
                      variant={
                        alerts.pendingChangeOrders?.count > 0 ? "warning" : "ok"
                      }
                      onClick={() =>
                        openAlertModal(
                          "pendingChangeOrders",
                          "Pending Change Orders",
                          "Change orders that are pending review or approval.",
                          alerts.pendingChangeOrders?.items || [],
                          "No pending change orders. All change orders have been reviewed or closed out.",
                        )
                      }
                    />
                  </>
                )}
              </div>
            </div>

            {/* 2. Upcoming Deadlines — desktop right col, row 1 */}
            <div className="admin-dash-section admin-dash-section-deadlines">
              <div className="admin-dash-section-header">
                <h2>Upcoming Deadlines</h2>
                {deadlines.length > 3 && (
                  <button
                    className="admin-dash-see-all"
                    onClick={openDeadlinesModal}
                  >
                    See all
                  </button>
                )}
              </div>
              <div className="admin-dash-section-content">
                {deadlinesLoading ? (
                  <LoadingSpinner message="Loading deadlines..." />
                ) : deadlines.length === 0 ? (
                  <p className="admin-dash-empty">
                    No selections due in the next 30 days.
                  </p>
                ) : (
                  deadlines
                    .slice(0, 3)
                    .map((d) => (
                      <DeadlineItem
                        key={d.id}
                        {...d}
                        onClick={handleDeadlineClick}
                      />
                    ))
                )}
              </div>
            </div>

            {/* 3. Add New Records — desktop left col, row 2 */}
            <div className="admin-dash-section admin-dash-section-records">
              <div className="admin-dash-section-header">
                <h2>Add New Records</h2>
              </div>
              <div className="admin-dash-section-content">
                <QuickActionButton
                  icon={<FaProjectDiagram />}
                  label="New Project"
                  onClick={() => openQuickAdd("project")}
                />
                <QuickActionButton
                  icon={<FaUserPlus />}
                  label="New Client"
                  onClick={() => openQuickAdd("client")}
                />
                <QuickActionButton
                  icon={<FaHardHat />}
                  label="New Vendor"
                  onClick={() => openQuickAdd("vendor")}
                />
              </div>
            </div>

            {/* 4. Upload Files — desktop right col, row 2 */}
            <div className="admin-dash-section admin-dash-section-uploads">
              <div className="admin-dash-section-header">
                <h2>Upload Files</h2>
              </div>
              <div className="admin-dash-section-content">
                <UploadCard
                  icon={<FaCamera />}
                  title="Progress Photos"
                  description="Construction progress photos"
                  onClick={() => handleUpload("progress-photos")}
                />
                <UploadCard
                  icon={<FaFileAlt />}
                  title="Selection Documents"
                  description="Quotes, invoices, spec sheets"
                  onClick={() => handleUpload("selection-docs")}
                />
                <UploadCard
                  icon={<FaFolderOpen />}
                  title="Project Documents"
                  description="Contracts, permits, official docs"
                  onClick={() => handleUpload("project-docs")}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MODALS */}
      <DashboardListModal
        isOpen={listModal.isOpen}
        onClose={closeListModal}
        title={listModal.title}
        subtitle={listModal.subtitle}
        items={listModal.items}
        renderItem={listModal.renderItem || (() => null)}
        emptyMessage={listModal.emptyMessage}
      />
      <ProjectModal
        isOpen={quickAddOpen.project}
        onClose={() => closeQuickAdd("project")}
        mode="quick"
        mutations={{ createProject: createProjectMutation }}
        clients={clientsForDropdown}
        clientsLoading={clientsLoading}
        projectManagers={pmList || []}
        pmLoading={pmLoading}
      />
      <ClientModal
        isOpen={quickAddOpen.client}
        onClose={() => closeQuickAdd("client")}
        mode="quick"
        mutations={{ createClient: createClientMutation, updateClient: null }}
      />
      <VendorModal
        isOpen={quickAddOpen.vendor}
        onClose={() => closeQuickAdd("vendor")}
        mode="quick"
        mutations={{ createVendor: createVendorMutation, updateVendor: null }}
      />
      <ProgressPhotoUploadModal
        isOpen={uploadModalOpen.progressPhotos}
        onClose={() => closeUploadModal("progressPhotos")}
        onUploadComplete={(r) => console.log("Progress photos uploaded:", r)}
        showTestAccounts={showTestAccounts}
      />
      <SelectionDocsUploadModal
        isOpen={uploadModalOpen.selectionDocs}
        onClose={() => closeUploadModal("selectionDocs")}
        onUploadComplete={(e) => console.log("Selection docs uploaded:", e)}
        showTestAccounts={showTestAccounts}
      />
      <ChangeOrderModal
        isOpen={!!coEditModal.changeOrderId && !!coData}
        onClose={() =>
          setCoEditModal({
            changeOrderId: null,
            projectId: null,
            projectName: "",
          })
        }
        projectId={coEditModal.projectId}
        projectName={coEditModal.projectName}
        changeOrder={coData || null}
        onSave={async (data, changeOrderId) => {
          if (changeOrderId) {
            await updateCOMutation.mutateAsync({
              changeOrderId,
              updates: data,
            });
          } else {
            await createCOMutation.mutateAsync(data);
          }
        }}
        isSaving={createCOMutation.isPending || updateCOMutation.isPending}
        fieldOptions={coFieldOptions}
      />
      <PlansDocsUploadModal
        isOpen={uploadModalOpen.projectDocs}
        onClose={() => closeUploadModal("projectDocs")}
        onUploadComplete={(r) => console.log("Project docs uploaded:", r)}
        showTestAccounts={showTestAccounts}
      />
    </>
  );
}

export default AdminDashboard;
